import React, { useEffect, useState } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FiCopy, FiSearch, FiX, FiEye, FiTrash2, FiChevronUp, FiChevronDown, FiShield, FiSmartphone } from "react-icons/fi";
import { useStickyState } from "../hooks/useStickyState";
import { useScrollRestoration } from "../hooks/useScrollRestoration";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const getUserId = (row) => row?.userId ?? row?.user_id ?? row?.userID ?? null;

const avatarColors = [
  "bg-blue-100 text-blue-600", "bg-purple-100 text-purple-600",
  "bg-green-100 text-green-600", "bg-orange-100 text-orange-600",
  "bg-rose-100 text-rose-600",  "bg-teal-100 text-teal-600",
];
const getAvatarColor = (name = "") => avatarColors[name.charCodeAt(0) % avatarColors.length];

const Spinner = ({ sm }) => (
  <div className={`border-2 border-t-transparent rounded-full animate-spin ${sm ? "w-3.5 h-3.5 border-current" : "w-6 h-6 border-blue-500"}`} />
);

const DuplicateAccount = () => {
  const navigate = useNavigate();
  const [duplicateAccounts, setDuplicateAccounts] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showModal, setShowModal]       = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pagination, setPagination]     = useState({});
  const [selectedIds, setSelectedIds]   = useState([]);
  const [currentUuid, setCurrentUuid]   = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [exemptBusy, setExemptBusy]       = useState(0);
  const [searchTerm, setSearchTerm]     = useStickyState("", "da_searchTerm");
  const [sortOrder, setSortOrder]       = useStickyState("", "da_sortOrder");
  const [currentPage, setCurrentPage]   = useStickyState(1, "da_currentPage");

  useEffect(() => { fetchDuplicateAccounts(currentPage, searchTerm, sortOrder); }, [currentPage, sortOrder]); // eslint-disable-line

  const fetchDuplicateAccounts = async (page = 1, search = searchTerm, sort = sortOrder) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let url = `${baseUrl}/users/multi-accounts?page=${page}&limit=50`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (sort)   url += `&deleteAccountCount=${sort}`;

      const res    = await fetch(url, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      const result = await res.json();
      if (res.ok && result.status) {
        setDuplicateAccounts(result.data || []);
        setPagination(result.pagination || {});
      } else {
        toast.error(result.message || "Failed to fetch duplicate accounts");
      }
    } catch { toast.error("Network error"); }
    finally   { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); if (currentPage === 1) fetchDuplicateAccounts(1, searchTerm, sortOrder); else setCurrentPage(1); };
  const handleClearSearch = () => { setSearchTerm(""); if (currentPage === 1) fetchDuplicateAccounts(1, "", sortOrder); else setCurrentPage(1); };
  const handleSortChange  = (order) => { setSortOrder(order); setCurrentPage(1); };

  useScrollRestoration("duplicate_account", !loading && duplicateAccounts.length > 0);

  const viewDetails = async (uuid) => {
    if (!uuid) { toast.error("Invalid UUID"); return; }
    setShowModal(true); setDetailLoading(true);
    setSelectedDetail(null); setSelectedIds([]); setCurrentUuid(uuid);
    try {
      const token  = localStorage.getItem("token");
      const res    = await fetch(`${baseUrl}/users/duplicate-accounts/${uuid}`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      const result = await res.json();
      if (res.ok && result.status) setSelectedDetail(result.data || result);
      else { toast.error(result.message || "Failed to fetch details"); setShowModal(false); }
    } catch { toast.error("Network error"); setShowModal(false); }
    finally   { setDetailLoading(false); }
  };

  const toggleSelectId  = (userId) => setSelectedIds((p) => p.includes(userId) ? p.filter((id) => id !== userId) : [...p, userId]);
  const toggleSelectAll = () => setSelectedIds(selectedIds.length === selectedDetail.length ? [] : selectedDetail.map((i) => i.userId));

  const handleDeleteAccounts = async () => {
    if (selectedIds.length === 0) { toast.error("Select at least one account"); return; }
    if (!currentUuid)             { toast.error("Invalid UUID"); return; }
    if (!window.confirm(`Delete ${selectedIds.length} account(s)?`)) return;

    setDeleteLoading(true);
    try {
      const token  = localStorage.getItem("token");
      const res    = await fetch(`${baseUrl}/users/remove-duplicate-accounts`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ uuid: currentUuid, ids: selectedIds }),
      });
      const result = await res.json();
      if (res.ok && result.status) {
        toast.success(result.message || "Deleted successfully");
        setSelectedIds([]);
        await viewDetails(currentUuid);
        await fetchDuplicateAccounts(pagination.currentPage || 1, searchTerm, sortOrder);
      } else {
        toast.error(result.message || "Failed to delete");
      }
    } catch { toast.error("Network error"); }
    finally   { setDeleteLoading(false); }
  };

  // Non-destructive: mark / unmark one account as exempt from uuid-based
  // multi-account detection. No account is deleted; the push device id is
  // untouched, so notifications keep working.
  const handleExempt = async (userId, exempt) => {
    if (!userId) { toast.error("Invalid user"); return; }
    if (!window.confirm(exempt
      ? "Exempt this account from multi-account / KYC detection? The account is kept and it will no longer be flagged on this device."
      : "Remove the exemption? This account will be re-flagged by multi-account detection.")) return;
    setExemptBusy(exemptBusy + 1); // any single in-flight guard
    const busy = exemptBusy;
    try {
      const token = localStorage.getItem("token");
      const res   = await fetch(`${baseUrl}/users/multi-account-exempt/${userId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ exempt }),
      });
      const result = await res.json();
      if (res.ok && result.status) {
        toast.success(result.message || "Updated");
        await viewDetails(currentUuid);
        await fetchDuplicateAccounts(pagination.currentPage || 1, searchTerm, sortOrder);
      } else {
        toast.error(result.message || "Failed to update exemption");
      }
    } catch { toast.error("Network error"); }
    finally { setExemptBusy(busy); }
  };

  // Clear the recorded device id for one account WITHOUT deleting the account.
  // The push token is kept, so the user keeps receiving notifications.
  const handleClearDevice = async (userId) => {
    if (!userId) { toast.error("Invalid user"); return; }
    if (!window.confirm("Clear the device id for this account? The account is kept and it keeps its push notifications; only the recorded device id is removed.")) return;
    setExemptBusy(exemptBusy + 1);
    const busy = exemptBusy;
    try {
      const token = localStorage.getItem("token");
      const res   = await fetch(`${baseUrl}/users/clear-device/${userId}`, { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      const result = await res.json();
      if (res.ok && result.status) {
        toast.success(result.message || "Device id cleared");
        await viewDetails(currentUuid);
      } else {
        toast.error(result.message || "Failed to clear device id");
      }
    } catch { toast.error("Network error"); }
    finally { setExemptBusy(busy); }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const getPaginationNumbers = (current, total) => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 3) return [1, 2, 3, 4, "...", total];
    if (current >= total - 2) return [1, "...", total - 3, total - 2, total - 1, total];
    return [1, "...", current - 1, current, current + 1, "...", total];
  };

  const totalPages = pagination.totalPages || pagination.lastPage || 1;

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">

          {/* Heading */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <FiCopy size={20} className="text-red-500" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Duplicate Accounts</h2>
              <p className="text-sm text-gray-400 mt-0.5">Review and remove multi-device accounts</p>
            </div>
          </div>

          {/* Main card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

            {/* Toolbar */}
            <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-700">
                Multi-Account Users
                {pagination.total > 0 && (
                  <span className="ml-2 text-xs font-normal text-gray-400">({pagination.total} total)</span>
                )}
              </h3>

              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search username, email..."
                    className="pl-8 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-56 sm:w-72"
                  />
                  {searchTerm && (
                    <button type="button" onClick={handleClearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <FiX size={13} />
                    </button>
                  )}
                </div>
                <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition">
                  Search
                </button>
              </form>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center items-center py-20 gap-3 text-gray-400 text-sm">
                  <Spinner /> Loading...
                </div>
              ) : duplicateAccounts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <FiCopy size={36} className="text-gray-300 mb-3" />
                  <p className="text-sm font-medium">No duplicate accounts found</p>
                  {searchTerm && <p className="text-xs mt-1">Try a different search term</p>}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3 w-10">#</th>
                      <th className="px-5 py-3">User</th>
                      <th className="px-5 py-3 hidden md:table-cell">UUID</th>
                      <th className="px-5 py-3 text-right hidden sm:table-cell">Coins</th>
                      <th className="px-5 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          Count
                          <div className="flex flex-col">
                            <button onClick={() => handleSortChange(sortOrder === "asc" ? "" : "asc")} className={`leading-none ${sortOrder === "asc" ? "text-blue-500" : "text-gray-300 hover:text-gray-500"}`}>
                              <FiChevronUp size={12} />
                            </button>
                            <button onClick={() => handleSortChange(sortOrder === "desc" ? "" : "desc")} className={`leading-none ${sortOrder === "desc" ? "text-blue-500" : "text-gray-300 hover:text-gray-500"}`}>
                              <FiChevronDown size={12} />
                            </button>
                          </div>
                        </div>
                      </th>
                      <th className="px-5 py-3 hidden lg:table-cell">Joined</th>
                      <th className="px-5 py-3 text-center w-24">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {duplicateAccounts.map((account, index) => {
                      const initials   = (account.username || "U")[0].toUpperCase();
                      const avatarCls  = getAvatarColor(account.username || "");
                      return (
                        <tr key={index} className="hover:bg-gray-50 transition">
                          <td className="px-5 py-3.5 text-gray-400 text-xs">
                            {(pagination.currentPage - 1) * (pagination.perPage || 50) + index + 1}
                          </td>
                          <td className="px-5 py-3.5">
                            {getUserId(account) ? (
                              <button
                                onClick={() => navigate(`/user-full-details/${getUserId(account)}`, { state: { from: '/duplicate-account' } })}
                                className="flex items-center gap-3 text-left group"
                                title="View user details"
                              >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarCls}`}>
                                  {initials}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-800 group-hover:text-blue-600 group-hover:underline transition truncate max-w-[140px]">{account.username || "—"}</p>
                                  <p className="text-xs text-gray-400 group-hover:text-blue-500 transition truncate max-w-[140px]">{account.email || "—"}</p>
                                </div>
                              </button>
                            ) : (
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarCls}`}>
                                  {initials}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-800 truncate max-w-[140px]">{account.username || "—"}</p>
                                  <p className="text-xs text-gray-400 truncate max-w-[140px]">{account.email || "—"}</p>
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-3.5 hidden md:table-cell">
                            <span className="font-mono text-xs text-gray-400 truncate block max-w-[160px]">{account.uuid || "—"}</span>
                          </td>
                          <td className="px-5 py-3.5 text-right font-semibold text-gray-700 hidden sm:table-cell">
                            {parseFloat(account.coin || 0).toFixed(2)}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              (account.deleteAccountCount || 0) > 1
                                ? "bg-red-100 text-red-600"
                                : "bg-orange-100 text-orange-500"
                            }`}>
                              {account.deleteAccountCount || 0}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-gray-500 text-xs whitespace-nowrap hidden lg:table-cell">
                            {formatDate(account.createdAt)}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <button
                              onClick={() => viewDetails(account.uuid || account.id || account.userId)}
                              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition mx-auto"
                            >
                              <FiEye size={13} /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {!loading && duplicateAccounts.length > 0 && (
              <div className="px-5 py-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-gray-400">
                  Page {pagination.currentPage || 1} of {totalPages} — {pagination.total || 0} records
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1.5">
                    <button
                      disabled={pagination.currentPage === 1}
                      onClick={() => setCurrentPage(pagination.currentPage - 1)}
                      className="px-3 py-1.5 text-sm border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40"
                    >
                      &laquo;
                    </button>
                    {getPaginationNumbers(pagination.currentPage, totalPages).map((pg, i) =>
                      pg === "..." ? (
                        <span key={i} className="px-1 text-gray-400 text-sm">…</span>
                      ) : (
                        <button
                          key={i}
                          onClick={() => setCurrentPage(pg)}
                          className={`px-3 py-1.5 text-sm border rounded-lg transition ${
                            pagination.currentPage === pg
                              ? "bg-blue-500 text-white border-blue-500"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          {pg}
                        </button>
                      )
                    )}
                    <button
                      disabled={!pagination.nextPage}
                      onClick={() => setCurrentPage(pagination.nextPage)}
                      className="px-3 py-1.5 text-sm border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40"
                    >
                      &raquo;
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white w-full max-w-5xl rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Duplicate Account Details</h2>
                {selectedDetail && (
                  <p className="text-xs text-gray-400 mt-0.5">{selectedDetail.length} account(s) share this device</p>
                )}
              </div>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition">
                <FiX size={18} />
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-auto p-6">
              {detailLoading ? (
                <div className="flex justify-center items-center py-16 gap-3 text-gray-400 text-sm">
                  <Spinner /> Loading details...
                </div>
              ) : selectedDetail && Array.isArray(selectedDetail) && selectedDetail.length > 0 ? (
                <>
                  {/* Selection action bar */}
                  {selectedIds.length > 0 && (
                    <div className="mb-4 flex items-center justify-between bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                      <span className="text-sm font-medium text-red-700">
                        {selectedIds.length} account{selectedIds.length > 1 ? "s" : ""} selected
                      </span>
                      <button
                        onClick={handleDeleteAccounts}
                        disabled={deleteLoading}
                        className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-xs font-medium px-4 py-2 rounded-lg transition"
                      >
                        {deleteLoading ? <><Spinner sm /> Deleting...</> : <><FiTrash2 size={13} /> Delete Selected</>}
                      </button>
                    </div>
                  )}

                  <div className="overflow-x-auto rounded-xl border border-gray-100">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                          <th className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.length === selectedDetail.length}
                              onChange={toggleSelectAll}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400"
                            />
                          </th>
                          <th className="px-4 py-3">#</th>
                          <th className="px-4 py-3">User</th>
                          <th className="px-4 py-3 hidden md:table-cell">UUID</th>
                          <th className="px-4 py-3 text-right">Coins</th>
                          <th className="px-4 py-3 text-center">Count</th>
                          <th className="px-4 py-3 hidden lg:table-cell">Joined</th>
                          <th className="px-4 py-3 text-center w-28">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {selectedDetail.map((item, index) => {
                          const checked    = selectedIds.includes(item.userId);
                          const initials   = (item.username || "U")[0].toUpperCase();
                          const avatarCls  = getAvatarColor(item.username || "");
                          return (
                            <tr key={index} className={`transition ${checked ? "bg-red-50" : "hover:bg-gray-50"}`}>
                              <td className="px-4 py-3.5">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleSelectId(item.userId)}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400"
                                />
                              </td>
                              <td className="px-4 py-3.5 text-gray-400 text-xs">{index + 1}</td>
                              <td className="px-4 py-3.5">
                                {item.userId ? (
                                  <button
                                    onClick={() => navigate(`/user-full-details/${item.userId}`, { state: { from: '/duplicate-account' } })}
                                    className="flex items-center gap-2.5 text-left group"
                                    title="View user details"
                                  >
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarCls}`}>
                                      {initials}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-medium text-gray-800 group-hover:text-blue-600 group-hover:underline transition truncate max-w-[120px]">{item.username || "—"}</p>
                                      <p className="text-xs text-gray-400 group-hover:text-blue-500 transition truncate max-w-[120px]">{item.email || "—"}</p>
                                    </div>
                                  </button>
                                ) : (
                                  <div className="flex items-center gap-2.5">
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarCls}`}>
                                      {initials}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-medium text-gray-800 truncate max-w-[120px]">{item.username || "—"}</p>
                                      <p className="text-xs text-gray-400 truncate max-w-[120px]">{item.email || "—"}</p>
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3.5 hidden md:table-cell">
                                <span className="font-mono text-xs text-gray-400 truncate block max-w-[140px]">{item.uuid || "—"}</span>
                              </td>
                              <td className="px-4 py-3.5 text-right font-semibold text-gray-700">
                                {parseFloat(item.coin || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3.5 text-center">
                                <div className="flex flex-col items-center gap-1">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                    (item.deleteAccountCount || 0) > 1 ? "bg-red-100 text-red-600" : "bg-orange-100 text-orange-500"
                                  }`}>
                                    {item.deleteAccountCount || 0}
                                  </span>
                                  {item.multiAccountExempt && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700" title="Exempt from multi-account / KYC detection">
                                      <FiShield size={10} /> Exempt
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3.5 text-xs text-gray-400 whitespace-nowrap hidden lg:table-cell">
                                {formatDate(item.createdAt)}
                              </td>
                              <td className="px-4 py-3.5 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {/* Exempt from multi-account / KYC detection (non-destructive) */}
                                  <button
                                    onClick={() => handleExempt(item.userId, !item.multiAccountExempt)}
                                    disabled={exemptBusy > 0 || !item.userId}
                                    className={`p-1.5 rounded-lg transition disabled:opacity-50 ${
                                      item.multiAccountExempt
                                        ? "bg-green-100 text-green-700 hover:bg-green-200"
                                        : "text-gray-400 hover:bg-green-50 hover:text-green-600"
                                    }`}
                                    title={item.multiAccountExempt
                                      ? "Exempt from multi-account detection (click to remove exemption)"
                                      : "Exempt from multi-account / KYC detection (account is kept)"}
                                  >
                                    <FiShield size={14} />
                                  </button>
                                  {/* Clear recorded device id (non-destructive, push token kept) */}
                                  <button
                                    onClick={() => handleClearDevice(item.userId)}
                                    disabled={exemptBusy > 0 || !item.userId}
                                    className="p-1.5 rounded-lg text-gray-400 hover:bg-amber-50 hover:text-amber-600 transition disabled:opacity-50"
                                    title="Clear device id (account kept, notifications kept)"
                                  >
                                    <FiSmartphone size={14} />
                                  </button>
                                  {/* Destructive: actually remove this account */}
                                  <button
                                    onClick={() => { setSelectedIds([item.userId]); setTimeout(handleDeleteAccounts, 0); }}
                                    disabled={deleteLoading || exemptBusy > 0}
                                    className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 disabled:opacity-50 transition"
                                    title="Delete account"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <FiCopy size={32} className="text-gray-300 mb-3" />
                  <p className="text-sm">No account details available</p>
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-5 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DuplicateAccount;
